import AddCommentIcon from '@mui/icons-material/AddComment'
import BeenhereIcon from '@mui/icons-material/Beenhere'
import DesignServicesIcon from '@mui/icons-material/DesignServices'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import HandymanIcon from '@mui/icons-material/Handyman'
import LabelIcon from '@mui/icons-material/Label'
import MoveDownIcon from '@mui/icons-material/MoveDown'
import SensorOccupiedIcon from '@mui/icons-material/SensorOccupied'
import StraightenIcon from '@mui/icons-material/Straighten'
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation'
import TransformIcon from '@mui/icons-material/Transform'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'

import { geschovenNaarAannemerWijzigbareDisciplines } from '@util/project-util'

import { useAuthenticatedUser } from '../../../../auth/hooks/useAuthenticatedUser'
import type { HoofdleidingProject } from '../../../project/types/HoofdleidingProject'
import type { OptionsDialogData } from '../../types/OptionsDialogData'
import { isHoofdleidingHerplanDoorlooptijdStatus } from '../../util/herplanning'

export interface Props {
  project: HoofdleidingProject
  anchorEl?: HTMLElement
  onClose: () => void
  onOpenDialog: (data: OptionsDialogData) => void
  selectableLabels: string[]
}

export const ProjectOptionsMenu = ({
  project,
  anchorEl,
  onClose,
  onOpenDialog,
  selectableLabels,
}: Props) => {
  const user = useAuthenticatedUser()
  const {
    canViewUpdateLabels,
    canCreateHerplanning,
    canCreateWijzigenAannemer,
    canCreateWijzigenDisciplines,
    canCreateProcesstap,
    canCreateReferentie,
    canCreateTechnischGereed,
    canCreateTracelengte,
    canCreateVerantwoordelijke,
    canCreateHoofdleidingGebiedswissel,
    canCreateWijzigenGeschovenNaarAannemer,
    organisatie,
  } = user

  const { statusText, verantwoordelijken, aannemers, netbeheerders } = project
  const isVerantwoordelijke =
    !!organisatie && verantwoordelijken.includes(organisatie)
  const isAannemer = !!organisatie && aannemers?.includes(organisatie)
  const isNetbeheerder = !!organisatie && netbeheerders.includes(organisatie)

  const canUserHerplanHoofdleiding =
    isHoofdleidingHerplanDoorlooptijdStatus(project.statusText) &&
    canCreateHerplanning &&
    isVerantwoordelijke

  const canChangeAannemer =
    statusText !== 'Geannuleerd' &&
    statusText !== 'Gereed' &&
    canCreateWijzigenAannemer &&
    isVerantwoordelijke

  const canChangeDisciplines =
    statusText !== 'Geannuleerd' &&
    statusText !== 'Gereed' &&
    canCreateWijzigenDisciplines &&
    isVerantwoordelijke

  const canUpdateLabels = canViewUpdateLabels && selectableLabels.length > 0

  const canUpdateProcesstap = canCreateProcesstap && isVerantwoordelijke

  const canTechnischGereedMelden =
    canCreateTechnischGereed &&
    statusText !== 'Gepauzeerd' &&
    statusText !== 'Geannuleerd' &&
    statusText !== 'Gereed' &&
    (isAannemer || isVerantwoordelijke)

  const canWijzigenVerantwoordelijke =
    isVerantwoordelijke &&
    canCreateVerantwoordelijke &&
    netbeheerders.length === 1

  const canWisselGebied =
    isVerantwoordelijke && canCreateHoofdleidingGebiedswissel

  const canUpdateTracelengte =
    canCreateTracelengte && (isVerantwoordelijke || isNetbeheerder)

  const canWijzigenGeschovenNaarAannemer =
    canCreateWijzigenGeschovenNaarAannemer &&
    geschovenNaarAannemerWijzigbareDisciplines(project, organisatie).length > 0

  const noOptionsHoofdleiding =
    !canChangeAannemer &&
    !canChangeDisciplines &&
    !canUpdateLabels &&
    !canUserHerplanHoofdleiding &&
    !canUpdateProcesstap &&
    !canTechnischGereedMelden &&
    !canCreateReferentie &&
    !canUpdateTracelengte &&
    !canWijzigenVerantwoordelijke &&
    !canWisselGebied &&
    !canWijzigenGeschovenNaarAannemer

  const handleOptionClick = (type: OptionsDialogData['type']) => {
    onClose()
    onOpenDialog({
      type,
      project,
    })
  }
  const handleLabelsClick = () => {
    onOpenDialog({
      type: 'labels',
      selectableLabels,
      project,
    })
    onClose()
  }

  const handleProcesstapClick = () => {
    onOpenDialog({
      project,
      type: 'hoofdleiding_processtap',
    })
    onClose()
  }

  const handleTechnischGereedClick = () => {
    onOpenDialog({
      project,
      type: 'technisch_gereed',
    })
    onClose()
  }

  return (
    <Menu
      id="menu-appbar"
      data-testid="menu"
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={Boolean(anchorEl)}
      onClose={onClose}
    >
      {canUserHerplanHoofdleiding && (
        <MenuItem onClick={() => handleOptionClick('herplanning')}>
          <ListItemIcon>
            <EventRepeatIcon />
          </ListItemIcon>
          <ListItemText primary={'Herplannen'} />
        </MenuItem>
      )}
      {canChangeAannemer && (
        <MenuItem onClick={() => handleOptionClick('hoofdleiding_aannemer')}>
          <ListItemIcon>
            <HandymanIcon />
          </ListItemIcon>
          <ListItemText primary={'Wijzig aannemer'} />
        </MenuItem>
      )}
      {canChangeDisciplines && (
        <MenuItem onClick={() => handleOptionClick('hoofdleiding_disciplines')}>
          <ListItemIcon>
            <TransferWithinAStationIcon />
          </ListItemIcon>
          <ListItemText primary={'Wijzig disciplines'} />
        </MenuItem>
      )}

      {canUpdateLabels && (
        <MenuItem onClick={handleLabelsClick}>
          <ListItemIcon>
            <LabelIcon />
          </ListItemIcon>
          <ListItemText primary="Wijzig labels" />
        </MenuItem>
      )}

      {canUpdateProcesstap && (
        <MenuItem onClick={handleProcesstapClick}>
          <ListItemIcon>
            <MoveDownIcon />
          </ListItemIcon>
          <ListItemText primary="Wijzig processtap" />
        </MenuItem>
      )}

      {canTechnischGereedMelden && (
        <MenuItem onClick={handleTechnischGereedClick}>
          <ListItemIcon>
            <BeenhereIcon />
          </ListItemIcon>
          <ListItemText primary="Technisch gereed melden" />
        </MenuItem>
      )}

      {canCreateReferentie && (
        <MenuItem onClick={() => handleOptionClick('hoofdleiding_referentie')}>
          <ListItemIcon>
            <AddCommentIcon />
          </ListItemIcon>
          <ListItemText primary="Wijzig netbeheerder referentie" />
        </MenuItem>
      )}
      {canUpdateTracelengte && (
        <MenuItem onClick={() => handleOptionClick('hoofdleiding_tracelengte')}>
          <ListItemIcon>
            <StraightenIcon />
          </ListItemIcon>
          <ListItemText primary="Wijzig tracélengte" />
        </MenuItem>
      )}
      {canWijzigenVerantwoordelijke && (
        <MenuItem
          onClick={() => handleOptionClick('hoofdleiding_verantwoordelijke')}
        >
          <ListItemIcon>
            <SensorOccupiedIcon />
          </ListItemIcon>
          <ListItemText primary="Wijzig verantwoordelijke" />
        </MenuItem>
      )}
      {canWisselGebied && (
        <MenuItem
          onClick={() => handleOptionClick('hoofdleiding_gebiedswissel')}
        >
          <ListItemIcon>
            <TransformIcon />
          </ListItemIcon>
          <ListItemText primary="Wijzig gebied" />
        </MenuItem>
      )}

      {canWijzigenGeschovenNaarAannemer && (
        <MenuItem
          onClick={() => handleOptionClick('wijzigen_geschoven_naar_aannemer')}
        >
          <ListItemIcon>
            <DesignServicesIcon />
          </ListItemIcon>
          <ListItemText primary="Wijzig geschoven naar aannemer" />
        </MenuItem>
      )}

      {noOptionsHoofdleiding && (
        <MenuItem disabled>
          <ListItemText primary="Geen opties beschikbaar" />
        </MenuItem>
      )}
    </Menu>
  )
}
